import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyPermission } from '../lib/permissions';
import { openProtectedFile } from '../lib/protectedFiles';
import CameraFileInput from '../components/CameraFileInput';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { appNowTime, toAppDateInputValue } from '../lib/appDateTime';
import {
  AlertCircle,
  ArrowLeft,
  Camera,
  CheckCircle2,
  Clock,
  Filter,
  History,
  Home,
  PauseCircle,
  PlayCircle,
  Plus,
  RefreshCw,
  Settings,
  Sparkles,
  Trash2,
  XCircle,
} from 'lucide-react';

const toDateInputValue = (date = new Date()) => {
  return toAppDateInputValue(date);
};

const today = () => toDateInputValue();
const nowTime = () => appNowTime();

const STATUS_LABELS = {
  pendente: 'Pendente',
  proxima: 'Próxima',
  atrasada: 'Atrasada',
  em_execucao: 'Em execução',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
  nao_realizada: 'Não realizada',
};

const FREQUENCY_LABELS = {
  diaria: 'Diária',
  dias_semana: 'Dias específicos',
  semanal: 'Semanal',
  intervalo: 'Intervalo',
  unica: 'Execução única',
};

const WEEKDAYS = [
  { value: 0, label: 'Seg' },
  { value: 1, label: 'Ter' },
  { value: 2, label: 'Qua' },
  { value: 3, label: 'Qui' },
  { value: 4, label: 'Sex' },
  { value: 5, label: 'Sáb' },
  { value: 6, label: 'Dom' },
];

const TIME_SLOTS = Array.from({ length: 14 }, (_, index) => index + 7);

const emptyEnclosure = {
  id: null,
  name: '',
  code: '',
  base_id: '',
  description: '',
  enclosure_type: '',
  sector: '',
  capacity: '',
  observations: '',
  active: true,
};

const emptySchedule = {
  id: null,
  base_id: '',
  enclosure_id: '',
  cleaning_type: 'Limpeza geral',
  frequency: 'diaria',
  weekdays: [],
  times: '08:00',
  shift: 'Manhã',
  interval_days: '',
  start_date: today(),
  end_date: '',
  photo_required: false,
  instructions: '',
  observations: '',
  active: true,
};

const getStatusKey = (task) => task.computed_status || task.status || 'pendente';

const getApiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.message || JSON.stringify(item)).join('; ');
  }
  return fallback;
};

const getStatusClass = (task) => {
  switch (getStatusKey(task)) {
    case 'concluida':
      return 'border-emerald-300 bg-emerald-50 text-emerald-800';
    case 'atrasada':
      return 'border-red-300 bg-red-50 text-red-800';
    case 'proxima':
      return 'border-amber-300 bg-amber-50 text-amber-800';
    case 'em_execucao':
      return 'border-blue-300 bg-blue-50 text-blue-800';
    case 'cancelada':
    case 'nao_realizada':
      return 'border-slate-300 bg-slate-100 text-slate-700';
    default:
      return 'border-slate-200 bg-white text-slate-700';
  }
};

export default function CleaningDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasAnyPermission(user, ['cleaning.manage']);
  const canCreateSchedule = hasAnyPermission(user, ['cleaning.manage', 'cleaning.schedule.create']);
  const canExecute = hasAnyPermission(user, ['cleaning.execute']);
  const canDelete = ['administrador', 'programador'].includes(user?.role);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bases, setBases] = useState([]);
  const [enclosures, setEnclosures] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyEnclosure, setHistoryEnclosure] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    date: today(),
    base_id: 'all',
    enclosure_id: 'all',
    sector: 'all',
    shift: 'all',
    status: 'all',
    responsible_id: 'all',
    cleaning_type: '',
  });
  const [enclosureForm, setEnclosureForm] = useState(emptyEnclosure);
  const [scheduleForm, setScheduleForm] = useState(emptySchedule);
  const [completeTask, setCompleteTask] = useState(null);
  const [completeNotes, setCompleteNotes] = useState('');
  const [completeFiles, setCompleteFiles] = useState([]);
  const [cancelTask, setCancelTask] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [sporadicOpen, setSporadicOpen] = useState(false);
  const [sporadicForm, setSporadicForm] = useState({
    enclosure_id: '',
    actual_date: today(),
    actual_time: nowTime(),
    cleaning_type: 'Limpeza geral',
    observations: '',
    reason: '',
  });
  const [sporadicFiles, setSporadicFiles] = useState([]);

  const sectors = useMemo(() => {
    return [...new Set(enclosures.map((item) => item.sector).filter(Boolean))].sort();
  }, [enclosures]);

  const filteredEnclosures = useMemo(() => {
    if (!filters.base_id || filters.base_id === 'all') return enclosures;
    return enclosures.filter((item) => item.base_id === filters.base_id);
  }, [enclosures, filters.base_id]);

  const groupedTasks = useMemo(() => {
    const groups = new Map();
    tasks.forEach((task) => {
      const key = task.enclosure_id || `sporadic-${task.id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          enclosure_id: task.enclosure_id,
          enclosure_name: task.enclosure_name,
          enclosure_code: task.enclosure_code,
          base_name: task.base_name,
          tasks: [],
        });
      }
      groups.get(key).tasks.push(task);
    });
    return Array.from(groups.values()).sort((a, b) => `${a.enclosure_name} ${a.enclosure_code}`.localeCompare(`${b.enclosure_name} ${b.enclosure_code}`));
  }, [tasks]);

  const fetchBases = async () => {
    const response = await axios.get(`${API}/bases`);
    setBases(response.data || []);
  };

  const fetchEnclosures = async () => {
    const response = await axios.get(`${API}/enclosures`);
    setEnclosures(response.data || []);
  };

  const fetchSchedules = async () => {
    const response = await axios.get(`${API}/cleaning-schedules`);
    setSchedules(response.data || []);
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });
      const response = await axios.get(`${API}/cleaning-dashboard?${params.toString()}`);
      setSummary(response.data.summary || null);
      setTasks(response.data.tasks || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar limpezas');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    Promise.all([fetchBases(), fetchEnclosures(), fetchSchedules()]).catch(() => {
      toast.error('Erro ao carregar cadastros de limpeza');
    });
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      ...(key === 'base_id' ? { enclosure_id: 'all' } : {}),
    }));
  };

  const resetEnclosureForm = () => setEnclosureForm(emptyEnclosure);
  const resetScheduleForm = () => setScheduleForm({ ...emptySchedule, base_id: filters.base_id === 'all' ? '' : filters.base_id });

  const saveEnclosure = async () => {
    if (!enclosureForm.name || !enclosureForm.code || !enclosureForm.base_id) {
      toast.error('Informe nome, identificação e base');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...enclosureForm,
        capacity: enclosureForm.capacity ? Number(enclosureForm.capacity) : null,
      };
      if (enclosureForm.id) {
        await axios.put(`${API}/enclosures/${enclosureForm.id}`, payload);
      } else {
        await axios.post(`${API}/enclosures`, payload);
      }
      toast.success('Recinto salvo');
      resetEnclosureForm();
      await fetchEnclosures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar recinto');
    } finally {
      setSaving(false);
    }
  };

  const setEnclosureStatus = async (enclosure, active) => {
    try {
      await axios.post(`${API}/enclosures/${enclosure.id}/status?active=${active}`);
      toast.success(active ? 'Recinto ativado' : 'Recinto inativado');
      await fetchEnclosures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar recinto');
    }
  };

  const deleteEnclosure = async (enclosure) => {
    if (!window.confirm(`Excluir o recinto ${enclosure.name} - ${enclosure.code}? O histórico será preservado.`)) return;
    try {
      await axios.delete(`${API}/enclosures/${enclosure.id}`);
      toast.success('Recinto excluído');
      await Promise.all([fetchEnclosures(), fetchSchedules(), fetchDashboard()]);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao excluir recinto'));
    }
  };

  const saveSchedule = async () => {
    if (!scheduleForm.base_id || !scheduleForm.enclosure_id || !scheduleForm.cleaning_type || !scheduleForm.start_date) {
      toast.error('Preencha base, recinto, tipo e início');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        base_id: scheduleForm.base_id,
        enclosure_id: scheduleForm.enclosure_id,
        cleaning_type: scheduleForm.cleaning_type,
        frequency: scheduleForm.frequency,
        weekdays: scheduleForm.weekdays,
        times: scheduleForm.times.split(',').map((item) => item.trim()).filter(Boolean),
        shift: scheduleForm.shift || null,
        interval_days: scheduleForm.interval_days ? Number(scheduleForm.interval_days) : null,
        start_date: scheduleForm.start_date,
        end_date: scheduleForm.end_date || null,
        photo_required: scheduleForm.photo_required,
        instructions: scheduleForm.instructions || null,
        observations: scheduleForm.observations || null,
        active: scheduleForm.active,
      };
      if (scheduleForm.id) {
        await axios.put(`${API}/cleaning-schedules/${scheduleForm.id}`, payload);
      } else {
        await axios.post(`${API}/cleaning-schedules`, payload);
      }
      toast.success('Programação salva');
      resetScheduleForm();
      await Promise.all([fetchSchedules(), fetchDashboard()]);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao salvar programação'));
    } finally {
      setSaving(false);
    }
  };

  const setScheduleStatus = async (schedule, active) => {
    try {
      await axios.post(`${API}/cleaning-schedules/${schedule.id}/status?active=${active}`);
      toast.success(active ? 'Programação ativada' : 'Programação inativada');
      await Promise.all([fetchSchedules(), fetchDashboard()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar programação');
    }
  };

  const deleteSchedule = async (schedule) => {
    if (!window.confirm(`Excluir a programação de ${schedule.enclosure_name}? As tarefas já geradas serão preservadas.`)) return;
    try {
      await axios.delete(`${API}/cleaning-schedules/${schedule.id}`);
      toast.success('Programação excluída');
      await Promise.all([fetchSchedules(), fetchDashboard()]);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao excluir programação'));
    }
  };

  const resetCleaningModule = async () => {
    if (!window.confirm('Apagar todos os recintos, programações e históricos de limpeza? Esta ação reinicia o módulo de limpeza.')) return;
    try {
      const response = await axios.delete(`${API}/cleaning-module/reset`);
      const deleted = response.data?.deleted || {};
      toast.success(`Limpeza concluída: ${deleted.enclosures || 0} recintos, ${deleted.cleaning_schedules || 0} programações e ${deleted.cleaning_executions || 0} tarefas removidas`);
      resetEnclosureForm();
      resetScheduleForm();
      setHistory([]);
      setHistoryEnclosure(null);
      await Promise.all([fetchEnclosures(), fetchSchedules(), fetchDashboard()]);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao limpar cadastros de limpeza'));
    }
  };

  const startTask = async (task) => {
    try {
      await axios.post(`${API}/cleaning-executions/${task.id}/start`);
      toast.success('Limpeza iniciada');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar limpeza');
    }
  };

  const uploadFiles = async (executionId, files) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/cleaning-executions/${executionId}/photos`, formData);
    }
  };

  const completeSelectedTask = async () => {
    if (!completeTask) return;
    const existingPhotoCount = completeTask.photos?.length || 0;
    if (completeTask.photo_required && existingPhotoCount + completeFiles.length === 0) {
      toast.error('Anexe ao menos uma foto para concluir esta limpeza');
      return;
    }
    setSaving(true);
    try {
      if (completeFiles.length > 0) {
        await uploadFiles(completeTask.id, completeFiles);
      }
      await axios.post(`${API}/cleaning-executions/${completeTask.id}/complete`, {
        actual_date: filters.date,
        actual_time: nowTime(),
        observations: completeNotes || null,
      });
      toast.success('Limpeza concluída');
      setCompleteTask(null);
      setCompleteNotes('');
      setCompleteFiles([]);
      fetchDashboard();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao concluir limpeza'));
    } finally {
      setSaving(false);
    }
  };

  const saveSporadic = async () => {
    if (!sporadicForm.enclosure_id || !sporadicForm.cleaning_type) {
      toast.error('Informe recinto e tipo de limpeza');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/cleaning-executions/sporadic`, sporadicForm);
      if (sporadicFiles.length > 0) {
        await uploadFiles(response.data.id, sporadicFiles);
      }
      toast.success('Limpeza esporádica registrada');
      setSporadicOpen(false);
      setSporadicFiles([]);
      setSporadicForm({ enclosure_id: '', actual_date: today(), actual_time: nowTime(), cleaning_type: 'Limpeza geral', observations: '', reason: '' });
      fetchDashboard();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao registrar limpeza'));
    } finally {
      setSaving(false);
    }
  };

  const cancelSelectedTask = async () => {
    if (!cancelTask || !cancelReason.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/cleaning-executions/${cancelTask.id}`, {
        status: 'cancelada',
        reason: cancelReason.trim(),
      });
      toast.success('Tarefa cancelada');
      setCancelTask(null);
      setCancelReason('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar tarefa');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (enclosure) => {
    try {
      const response = await axios.get(`${API}/enclosures/${enclosure.id}/history`);
      setHistoryEnclosure(response.data.enclosure);
      setHistory(response.data.history || []);
      setActiveTab('history');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar histórico');
    }
  };

  const openPhoto = async (photo) => {
    try {
      await openProtectedFile(photo.file_url, photo.filename);
    } catch (error) {
      toast.error(error.message || 'Erro ao abrir foto');
    }
  };

  const deletePhoto = async (executionId, photo) => {
    if (!photo?.file_url) return;
    if (!window.confirm('Excluir esta foto da limpeza?')) return;
    try {
      await axios.delete(`${API}/cleaning-executions/${executionId}/photos`, {
        params: { file_url: photo.file_url }
      });
      toast.success('Foto excluída');
      await fetchDashboard();
      if (historyEnclosure) {
        await openHistory(historyEnclosure);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir foto');
    }
  };

  const getTaskHour = (task) => {
    const time = task.scheduled_time || task.actual_time || '';
    const hour = Number.parseInt(time.slice(0, 2), 10);
    return Number.isNaN(hour) ? null : hour;
  };

  const renderTaskTile = (task, compact = false) => (
    <div key={task.id} className={`rounded-md border p-2 ${getStatusClass(task)}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-slate-900">{task.cleaning_type}</p>
          <p className="text-[11px] text-slate-600">{task.scheduled_time || 'Esporádica'}{task.actual_time ? ` • ${task.actual_time}` : ''}</p>
        </div>
        <Badge className="bg-white px-1.5 py-0 text-[10px] text-slate-700">{STATUS_LABELS[getStatusKey(task)] || getStatusKey(task)}</Badge>
      </div>
      {!compact && task.observations && <p className="mt-1 text-xs text-slate-700">{task.observations}</p>}
      <div className="mt-2 flex flex-wrap gap-1">
        {canExecute && !['concluida', 'cancelada', 'nao_realizada'].includes(task.status) && (
          <>
            <Button size="sm" variant="outline" onClick={() => startTask(task)} className="h-8 px-2 text-xs">
              <PlayCircle className="mr-1 h-3 w-3" />
              Iniciar
            </Button>
            <Button size="sm" onClick={() => setCompleteTask(task)} className="h-8 bg-emerald-700 px-2 text-xs hover:bg-emerald-800">
              <CheckCircle2 className="mr-1 h-3 w-3" />
              Concluir
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCancelTask(task)} className="h-8 px-2 text-xs text-red-700">
              <XCircle className="mr-1 h-3 w-3" />
              Cancelar
            </Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={() => openHistory({ id: task.enclosure_id })} className="h-8 px-2 text-xs">
          <History className="mr-1 h-3 w-3" />
          Histórico
        </Button>
      </div>
      {task.photos?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {task.photos.map((photo) => (
            <span key={photo.id || photo.file_url} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-700">
              <button type="button" onClick={() => openPhoto(photo)} className="hover:underline">
                <Camera className="mr-1 inline h-3 w-3" />
                {photo.filename || 'Foto'}
              </button>
              {canDelete && (
                <button type="button" onClick={() => deletePhoto(task.id, photo)} className="text-red-600" title="Excluir foto">
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Limpeza de Recintos</h1>
              <p className="text-sm text-slate-500">Acompanhamento operacional, rotinas e histórico</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowFilters((current) => !current)}>
              <Filter className="mr-2 h-4 w-4" />
              Filtros
            </Button>
            {canExecute && (
              <Button onClick={() => setSporadicOpen(true)} className="bg-emerald-700 hover:bg-emerald-800">
                <Plus className="mr-2 h-4 w-4" />
                Esporádica
              </Button>
            )}
            <Button variant="outline" onClick={fetchDashboard}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Atualizar
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-5 px-4 py-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              ['dashboard', 'Acompanhamento', Clock],
              ['history', 'Histórico', History],
              ...(canCreateSchedule ? [['admin', canManage ? 'Administração' : 'Programações', Settings]] : []),
            ].map(([id, label, Icon]) => (
              <Button key={id} variant={activeTab === id ? 'default' : 'outline'} onClick={() => setActiveTab(id)} className={activeTab === id ? 'bg-slate-900 hover:bg-slate-800' : ''}>
                <Icon className="mr-2 h-4 w-4" />
                {label}
              </Button>
            ))}
          </div>
          {activeTab === 'dashboard' && (
            <div className="w-full rounded-md border bg-white p-3 lg:w-64">
              <Label>Data do acompanhamento</Label>
              <Input type="date" value={filters.date} onChange={(event) => updateFilter('date', event.target.value)} />
            </div>
          )}
        </div>

        {showFilters && (
          <Card>
            <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
              <div>
                <Label>Data</Label>
                <Input type="date" value={filters.date} onChange={(event) => updateFilter('date', event.target.value)} />
              </div>
              <div>
                <Label>Base</Label>
                <Select value={filters.base_id} onValueChange={(value) => updateFilter('base_id', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {bases.map((base) => <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Recinto</Label>
                <Select value={filters.enclosure_id} onValueChange={(value) => updateFilter('enclosure_id', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {filteredEnclosures.map((item) => <SelectItem key={item.id} value={item.id}>{item.name} - {item.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Setor</Label>
                <Select value={filters.sector} onValueChange={(value) => updateFilter('sector', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {sectors.map((sector) => <SelectItem key={sector} value={sector}>{sector}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Turno</Label>
                <Select value={filters.shift} onValueChange={(value) => updateFilter('shift', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {['Manhã', 'Tarde', 'Noite'].map((shift) => <SelectItem key={shift} value={shift}>{shift}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={filters.status} onValueChange={(value) => updateFilter('status', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Tipo de limpeza</Label>
                <Input value={filters.cleaning_type} onChange={(event) => updateFilter('cleaning_type', event.target.value)} placeholder="Buscar por tipo" />
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'dashboard' && (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              {[
                ['Programadas', summary?.scheduled || 0, Clock, 'text-slate-700'],
                ['Concluídas', summary?.completed || 0, CheckCircle2, 'text-emerald-700'],
                ['Pendentes', summary?.pending || 0, PauseCircle, 'text-amber-700'],
                ['Atrasadas', summary?.overdue || 0, AlertCircle, 'text-red-700'],
                ['Canceladas', summary?.cancelled_or_not_done || 0, XCircle, 'text-slate-700'],
              ].map(([label, value, Icon, color]) => (
                <Card key={label}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs text-slate-500">{label}</p>
                      <p className="text-2xl font-semibold text-slate-900">{value}</p>
                    </div>
                    <Icon className={`h-6 w-6 ${color}`} />
                  </CardContent>
                </Card>
              ))}
            </div>

            {loading ? (
              <Card><CardContent className="p-6 text-sm text-slate-500">Carregando limpezas...</CardContent></Card>
            ) : tasks.length === 0 ? (
              <Card><CardContent className="p-6 text-sm text-slate-500">Nenhuma limpeza para os filtros atuais.</CardContent></Card>
            ) : (
              <>
                <Card className="hidden overflow-hidden lg:block">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[1500px] text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="sticky left-0 z-20 w-64 min-w-64 border-r bg-slate-100 p-3 text-left font-semibold text-slate-700">Recinto</th>
                            {TIME_SLOTS.map((hour) => (
                              <th key={hour} className="min-w-[88px] border-r border-slate-200 p-3 text-center font-semibold text-slate-700">
                                {String(hour).padStart(2, '0')}:00
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {groupedTasks.map((group, index) => (
                            <tr key={group.enclosure_id || group.enclosure_name} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="sticky left-0 z-10 w-64 border-r bg-inherit p-3 align-top">
                                <div className="flex items-start gap-2">
                                  <Home className="mt-0.5 h-4 w-4 text-teal-700" />
                                  <div>
                                    <p className="font-semibold text-slate-900">{group.enclosure_name} - {group.enclosure_code}</p>
                                    <p className="text-xs text-slate-500">{group.base_name || 'Base'}</p>
                                    <Button size="sm" variant="outline" onClick={() => openHistory({ id: group.enclosure_id })} className="mt-2 h-8 px-2 text-xs">
                                      <History className="mr-1 h-3 w-3" />
                                      Histórico
                                    </Button>
                                  </div>
                                </div>
                              </td>
                              {TIME_SLOTS.map((hour) => {
                                const hourTasks = group.tasks.filter((task) => getTaskHour(task) === hour);
                                return (
                                  <td key={hour} className="border-r border-slate-100 p-2 align-top">
                                    {hourTasks.length === 0 ? (
                                      <div className="flex min-h-[74px] items-center justify-center rounded bg-slate-50 text-slate-300">-</div>
                                    ) : (
                                      <div className="space-y-2">
                                        {hourTasks.map((task) => renderTaskTile(task, true))}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-4 lg:hidden">
                  {groupedTasks.map((group) => (
                    <Card key={group.enclosure_id || group.enclosure_name}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">
                          {group.enclosure_name} - {group.enclosure_code}
                          <span className="mt-1 block text-xs font-normal text-slate-500">{group.base_name || 'Base'}</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {TIME_SLOTS.map((hour) => {
                          const hourTasks = group.tasks.filter((task) => getTaskHour(task) === hour);
                          if (hourTasks.length === 0) return null;
                          return (
                            <div key={hour}>
                              <p className="mb-1 text-xs font-semibold text-slate-500">{String(hour).padStart(2, '0')}:00</p>
                              <div className="space-y-2">{hourTasks.map((task) => renderTaskTile(task))}</div>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-col gap-3 text-lg sm:flex-row sm:items-center sm:justify-between">
                <span>Histórico {historyEnclosure ? `- ${historyEnclosure.name}` : ''}</span>
                <Select value={historyEnclosure?.id || ''} onValueChange={(value) => openHistory(enclosures.find((item) => item.id === value))}>
                  <SelectTrigger className="sm:w-80"><SelectValue placeholder="Selecionar recinto" /></SelectTrigger>
                  <SelectContent>
                    {enclosures.map((item) => <SelectItem key={item.id} value={item.id}>{item.name} - {item.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {history.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum histórico carregado.</p>
              ) : history.map((item) => (
                <div key={item.id} className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="font-medium text-slate-900">{item.scheduled_date || item.actual_date} • {item.scheduled_time || item.actual_time || '-'}</p>
                    <Badge className={item.status === 'concluida' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>{STATUS_LABELS[item.status] || item.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">{item.cleaning_type} • {item.responsible_name || item.registered_by_name || 'Responsável não informado'}</p>
                  {item.reason && <p className="text-sm text-red-700">Motivo: {item.reason}</p>}
                  {item.observations && <p className="mt-1 text-sm text-slate-700">{item.observations}</p>}
                  {item.photos?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {item.photos.map((photo) => (
                        <span key={photo.id || photo.file_url} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs">
                          <button type="button" onClick={() => openPhoto(photo)} className="hover:underline">{photo.filename || 'Foto'}</button>
                          {canDelete && (
                            <button type="button" onClick={() => deletePhoto(item.id, photo)} className="text-red-600" title="Excluir foto">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {activeTab === 'admin' && canCreateSchedule && (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {canDelete && (
              <Card className="border-red-200 xl:col-span-2">
                <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-red-900">Reiniciar módulo de limpeza</p>
                    <p className="text-sm text-red-700">Remove recintos, programações e tarefas/históricos atuais para iniciar os cadastros novamente.</p>
                  </div>
                  <Button variant="outline" onClick={resetCleaningModule} className="border-red-300 text-red-700 hover:bg-red-50">
                    Limpar cadastros
                  </Button>
                </CardContent>
              </Card>
            )}
            {canManage && <Card>
              <CardHeader><CardTitle className="text-lg">Recintos</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div><Label>Nome</Label><Input value={enclosureForm.name} onChange={(e) => setEnclosureForm((c) => ({ ...c, name: e.target.value }))} /></div>
                  <div><Label>Identificação</Label><Input value={enclosureForm.code} onChange={(e) => setEnclosureForm((c) => ({ ...c, code: e.target.value }))} /></div>
                  <div>
                    <Label>Base</Label>
                    <Select value={enclosureForm.base_id} onValueChange={(value) => setEnclosureForm((c) => ({ ...c, base_id: value }))}>
                      <SelectTrigger><SelectValue placeholder="Base" /></SelectTrigger>
                      <SelectContent>{bases.map((base) => <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Setor</Label><Input value={enclosureForm.sector} onChange={(e) => setEnclosureForm((c) => ({ ...c, sector: e.target.value }))} /></div>
                  <div><Label>Tipo</Label><Input value={enclosureForm.enclosure_type} onChange={(e) => setEnclosureForm((c) => ({ ...c, enclosure_type: e.target.value }))} /></div>
                  <div><Label>Capacidade</Label><Input type="number" value={enclosureForm.capacity} onChange={(e) => setEnclosureForm((c) => ({ ...c, capacity: e.target.value }))} /></div>
                  <div className="md:col-span-2"><Label>Observações</Label><Textarea value={enclosureForm.observations} onChange={(e) => setEnclosureForm((c) => ({ ...c, observations: e.target.value }))} /></div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveEnclosure} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{enclosureForm.id ? 'Salvar alterações' : 'Criar recinto'}</Button>
                  <Button variant="outline" onClick={resetEnclosureForm}>Limpar</Button>
                </div>
                <div className="max-h-[360px] space-y-2 overflow-y-auto">
                  {enclosures.map((item) => (
                    <div key={item.id} className="flex flex-col gap-2 rounded border p-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-medium">{item.name} - {item.code}</p>
                        <p className="text-xs text-slate-500">{item.base_name || item.base_id} • {item.sector || 'Sem setor'}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEnclosureForm({ ...emptyEnclosure, ...item, capacity: item.capacity || '' })}>Editar</Button>
                        <Button size="sm" variant="outline" onClick={() => openHistory(item)}>Histórico</Button>
                        <Button size="sm" variant="outline" onClick={() => setEnclosureStatus(item, !item.active)}>{item.active ? 'Inativar' : 'Ativar'}</Button>
                        {canDelete && (
                          <Button size="sm" variant="outline" onClick={() => deleteEnclosure(item)} className="text-red-700">Excluir</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>}

            <Card>
              <CardHeader><CardTitle className="text-lg">Programações</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <Label>Base</Label>
                    <Select value={scheduleForm.base_id} onValueChange={(value) => setScheduleForm((c) => ({ ...c, base_id: value, enclosure_id: '' }))}>
                      <SelectTrigger><SelectValue placeholder="Base" /></SelectTrigger>
                      <SelectContent>{bases.map((base) => <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Recinto</Label>
                    <Select value={scheduleForm.enclosure_id} onValueChange={(value) => setScheduleForm((c) => ({ ...c, enclosure_id: value }))}>
                      <SelectTrigger><SelectValue placeholder="Recinto" /></SelectTrigger>
                      <SelectContent>{enclosures.filter((item) => !scheduleForm.base_id || item.base_id === scheduleForm.base_id).map((item) => <SelectItem key={item.id} value={item.id}>{item.name} - {item.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Tipo</Label><Input value={scheduleForm.cleaning_type} onChange={(e) => setScheduleForm((c) => ({ ...c, cleaning_type: e.target.value }))} /></div>
                  <div>
                    <Label>Frequência</Label>
                    <Select value={scheduleForm.frequency} onValueChange={(value) => setScheduleForm((c) => ({ ...c, frequency: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(FREQUENCY_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Horários</Label><Input value={scheduleForm.times} onChange={(e) => setScheduleForm((c) => ({ ...c, times: e.target.value }))} placeholder="08:00, 14:00" /></div>
                  <div><Label>Turno</Label><Input value={scheduleForm.shift} onChange={(e) => setScheduleForm((c) => ({ ...c, shift: e.target.value }))} /></div>
                  <div><Label>Início</Label><Input type="date" value={scheduleForm.start_date} onChange={(e) => setScheduleForm((c) => ({ ...c, start_date: e.target.value }))} /></div>
                  <div><Label>Fim</Label><Input type="date" value={scheduleForm.end_date} onChange={(e) => setScheduleForm((c) => ({ ...c, end_date: e.target.value }))} /></div>
                  {scheduleForm.frequency === 'intervalo' && <div><Label>Intervalo em dias</Label><Input type="number" value={scheduleForm.interval_days} onChange={(e) => setScheduleForm((c) => ({ ...c, interval_days: e.target.value }))} /></div>}
                  <div className="flex items-end gap-2">
                    <Checkbox checked={scheduleForm.photo_required} onCheckedChange={(checked) => setScheduleForm((c) => ({ ...c, photo_required: Boolean(checked) }))} />
                    <Label>Foto obrigatória</Label>
                  </div>
                  {['dias_semana', 'semanal'].includes(scheduleForm.frequency) && (
                    <div className="md:col-span-2">
                      <Label>Dias</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {WEEKDAYS.map((day) => (
                          <Button key={day.value} type="button" size="sm" variant={scheduleForm.weekdays.includes(day.value) ? 'default' : 'outline'} onClick={() => setScheduleForm((current) => ({ ...current, weekdays: current.weekdays.includes(day.value) ? current.weekdays.filter((item) => item !== day.value) : [...current.weekdays, day.value] }))}>
                            {day.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="md:col-span-2"><Label>Instruções</Label><Textarea value={scheduleForm.instructions} onChange={(e) => setScheduleForm((c) => ({ ...c, instructions: e.target.value }))} /></div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveSchedule} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{scheduleForm.id ? 'Salvar programação' : 'Criar programação'}</Button>
                  <Button variant="outline" onClick={resetScheduleForm}>Limpar</Button>
                </div>
                <div className="max-h-[360px] space-y-2 overflow-y-auto">
                  {schedules.map((item) => (
                    <div key={item.id} className="flex flex-col gap-2 rounded border p-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-medium">{item.enclosure_name} • {item.cleaning_type}</p>
                        <p className="text-xs text-slate-500">{FREQUENCY_LABELS[item.frequency] || item.frequency} • {(item.times || []).join(', ')} • {item.active ? 'Ativa' : 'Inativa'}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {canManage && <Button size="sm" variant="outline" onClick={() => setScheduleForm({ ...emptySchedule, ...item, times: (item.times || []).join(', '), interval_days: item.interval_days || '', end_date: item.end_date || '' })}>Editar</Button>}
                        {canManage && <Button size="sm" variant="outline" onClick={() => setScheduleStatus(item, !item.active)}>{item.active ? 'Inativar' : 'Ativar'}</Button>}
                        {canDelete && (
                          <Button size="sm" variant="outline" onClick={() => deleteSchedule(item)} className="text-red-700">Excluir</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      <Dialog open={Boolean(completeTask)} onOpenChange={(open) => !open && setCompleteTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Concluir limpeza</DialogTitle>
            <DialogDescription>{completeTask?.enclosure_name} • {completeTask?.cleaning_type}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea placeholder="Observações" value={completeNotes} onChange={(e) => setCompleteNotes(e.target.value)} />
            <CameraFileInput multiple accept="image/*" onFilesSelected={setCompleteFiles} fileLabel="Anexar fotos" cameraLabel="Tirar foto" />
            {completeFiles.length > 0 && <p className="text-xs text-slate-500">{completeFiles.length} foto(s) selecionada(s)</p>}
            <Button onClick={completeSelectedTask} disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800">
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Salvar conclusão
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(cancelTask)} onOpenChange={(open) => !open && setCancelTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar tarefa</DialogTitle>
            <DialogDescription>{cancelTask?.enclosure_name} • {cancelTask?.cleaning_type}</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Motivo obrigatório" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          <Button onClick={cancelSelectedTask} disabled={saving || !cancelReason.trim()} className="bg-red-700 hover:bg-red-800">Cancelar tarefa</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={sporadicOpen} onOpenChange={setSporadicOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Limpeza esporádica</DialogTitle>
            <DialogDescription>Registro imediato no histórico do recinto</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Recinto</Label>
              <Select value={sporadicForm.enclosure_id} onValueChange={(value) => setSporadicForm((c) => ({ ...c, enclosure_id: value }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{enclosures.filter((item) => item.active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name} - {item.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data</Label><Input type="date" value={sporadicForm.actual_date} onChange={(e) => setSporadicForm((c) => ({ ...c, actual_date: e.target.value }))} /></div>
            <div><Label>Hora</Label><Input type="time" value={sporadicForm.actual_time} onChange={(e) => setSporadicForm((c) => ({ ...c, actual_time: e.target.value }))} /></div>
            <div className="sm:col-span-2"><Label>Tipo</Label><Input value={sporadicForm.cleaning_type} onChange={(e) => setSporadicForm((c) => ({ ...c, cleaning_type: e.target.value }))} /></div>
            <div className="sm:col-span-2"><Label>Motivo</Label><Input value={sporadicForm.reason} onChange={(e) => setSporadicForm((c) => ({ ...c, reason: e.target.value }))} /></div>
            <div className="sm:col-span-2"><Label>Observações</Label><Textarea value={sporadicForm.observations} onChange={(e) => setSporadicForm((c) => ({ ...c, observations: e.target.value }))} /></div>
            <div className="sm:col-span-2">
              <CameraFileInput multiple accept="image/*" onFilesSelected={setSporadicFiles} fileLabel="Anexar fotos" cameraLabel="Tirar foto" />
              {sporadicFiles.length > 0 && <p className="mt-1 text-xs text-slate-500">{sporadicFiles.length} foto(s) selecionada(s)</p>}
            </div>
          </div>
          <Button onClick={saveSporadic} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
            <Sparkles className="mr-2 h-4 w-4" />
            Registrar limpeza
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
